/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react';
import { Combobox, ComboboxInput } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Link, useSearchParams } from 'react-router-dom';
import { type ISolutionDeploymentListItem } from '../../interfaces/ISolutionDeployment';
import SolutionDemoItem, { SolutionDemoItemHeader } from './SolutionDemoItem';
import { useSelector } from 'react-redux';
import { selectAllSolutions, type ISolution } from '../../reducers/solutionsSlice';

// Filter types
type StatusFilter = 'pending' | 'creating' | 'ready' | 'updating' | 'deleting' | 'deleted' | 'errored';

interface FilterButtonProps {
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, icon = undefined, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-2 py-0.5 gap-1 rounded-sm text-sm font-medium transition-colors ${
        isActive ? 'bg-dataops-rich-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon && <div>{icon}</div>}
      <div>{label}</div>
    </button>
  );
};

// Filter configuration
const FILTER_CONFIG: Array<{
  key: StatusFilter;
  label: string;
  icon?: React.ReactNode;
}> = [
  { key: 'pending', label: 'Pending', icon: <div className="h-2 w-2 bg-dataops-taupe-gray rounded-full" /> },
  { key: 'creating', label: 'Creating', icon: <div className="h-2 w-2 bg-dataops-saffron-yellow rounded-full" /> },
  { key: 'ready', label: 'Ready', icon: <div className="h-2 w-2 bg-dataops-forest-green rounded-full" /> },
  { key: 'updating', label: 'Updating', icon: <div className="h-2 w-2 bg-dataops-saffron-yellow rounded-full" /> },
  { key: 'deleting', label: 'Deleting', icon: <div className="h-2 w-2 bg-dataops-saffron-yellow rounded-full" /> },
  { key: 'deleted', label: 'Deleted', icon: <div className="h-2 w-2 bg-dataops-rich-black rounded-full" /> },
  { key: 'errored', label: 'Errored', icon: <div className="h-2 w-2 bg-dataops-crayola-red rounded-full" /> },
];

interface SolutionDemosListProps {
  solutonDeployments: ISolutionDeploymentListItem[];
  getAccountName: (accountId: number) => string;
}

export default function SolutionDemosList({ solutonDeployments, getAccountName }: SolutionDemosListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<StatusFilter>>(new Set());
  const solutions = useSelector(selectAllSolutions);

  // Initialize search value and filters from URL params
  useEffect(() => {
    setSearchValue(searchParams.get('search') ?? '');

    // Initialize filters from URL params
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      const filters = filtersParam.split(',') as StatusFilter[];
      setActiveFilters(new Set(filters));
    }
  }, [searchParams]);

  // Debounce search value
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchValue]);

  // Handle search input change
  function onSearchInputChange(value: string) {
    setSearchValue(value);
    updateUrlParams(value, activeFilters);
  }

  // Handle filter toggle
  function toggleFilter(filter: StatusFilter) {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
    updateUrlParams(searchValue, newFilters);
  }

  // Update URL params with search and filters
  function updateUrlParams(search: string, filters: Set<StatusFilter>) {
    const params: Record<string, string> = {};

    if (search.trim()) {
      params.search = search;
    }

    if (filters.size > 0) {
      params.filters = Array.from(filters).join(',');
    }

    setSearchParams(params);
  }

  // Filter and sort all solutonDeployments based on search and filters
  const filteredDeployments = useMemo(() => {
    const deploymentList = Array.isArray(solutonDeployments) ? solutonDeployments : [];

    // Apply search filter if there's a search term
    let searchFilteredDeployments = deploymentList;
    if (debouncedSearchValue.trim()) {
      const searchTerm = debouncedSearchValue.toLowerCase();
      searchFilteredDeployments = deploymentList.filter((deployment) => {
        const accountName = getAccountName(deployment.snowflake_account_id).toLowerCase();

        // Get the solution name for this deployment
        const solution = solutions.find((sol) => sol.id === deployment.project_id.toString());
        const solutionName = solution?.listing?.name?.toLowerCase() ?? '';

        return (
          deployment.solution_prefix?.toLowerCase().includes(searchTerm) ||
          accountName.includes(searchTerm) ||
          solutionName.includes(searchTerm)
        );
      });
    }

    // Apply status filters
    let statusFilteredDeployments = searchFilteredDeployments;
    if (activeFilters.size > 0) {
      statusFilteredDeployments = searchFilteredDeployments.filter((deployment) => {
        const deploymentStatus = deployment.status.toLowerCase() as StatusFilter;
        return activeFilters.has(deploymentStatus);
      });
    }

    // Create a copy of the array before sorting to avoid mutating the original
    // Sort by created date (newest first)
    return [...statusFilteredDeployments].sort((a, b) => {
      const createdDateA = new Date(a.created_at).getTime();
      const createdDateB = new Date(b.created_at).getTime();
      return createdDateB - createdDateA;
    });
  }, [solutonDeployments, debouncedSearchValue, activeFilters, getAccountName, solutions]);

  // Render search box component
  const renderSearchBox = () => (
    <div className="flex items-center gap-6">
      <div className="grow">
        <div className="flex flex-row gap-2">
          <div className="h-[33px] relative flex-1 rounded-md bg-dataops-supporting-gray/50">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-2 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
            <Combobox>
              <ComboboxInput
                value={searchValue}
                aria-label="search-solution-deployments-input"
                placeholder="Type to search by name, solution name, or account"
                className="h-[33px] w-full border border-gray-200 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-500 ring-0 focus:ring-0 sm:text-sm rounded-md outline-none outline-offset-0 focus:outline focus:outline-dataops-primary-light-blue/20 focus:outline-4 transition-all ease-in-out"
                data-testid="search-solution-deployments-input"
                onChange={(event) => {
                  onSearchInputChange(event.target.value);
                }}
              />
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );

  // Render filter buttons component
  const renderFilterButtons = () => (
    <div className="flex flex-wrap gap-2">
      {FILTER_CONFIG.map(({ key, label, icon }) => (
        <FilterButton
          key={key}
          label={label}
          icon={icon}
          isActive={activeFilters.has(key)}
          onClick={() => {
            toggleFilter(key);
          }}
        />
      ))}
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (debouncedSearchValue.trim() && filteredDeployments.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No solution demos found matching &ldquo;{debouncedSearchValue}&rdquo;</p>
        </div>
      );
    }

    if (filteredDeployments.length > 0) {
      return (
        <div className="">
          <SolutionDemoItemHeader />
          <ul role="list" className="w-full space-y-1">
            {filteredDeployments.map((deployment) => (
              <li key={deployment.id}>
                <SolutionDemoItem
                  deployment={deployment}
                  accountName={getAccountName(deployment.snowflake_account_id)}
                />
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No solution demos available</p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderSearchBox()}
      {renderFilterButtons()}
      {renderContent()}
    </div>
  );
}
